import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Fade from '@mui/material/Fade';
import type { GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import SegmentedTabs from 'components/UI/SegmentedTabs';
import { useSelectedBusinessUnit } from 'contexts/SidebarContext';
import type React from 'react';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGetBrokerPoliciesQuery } from 'services/api/brokerDashboard.api';
import { useGetInsuranceDashboardSummaryQuery } from 'services/api/insuranceDashboard.api';
import { NAVY, RADIUS_COIN, SURFACE_CONTAINER_LOW } from 'styles/tokens';

import BrokerLeadsView from './components/BrokerLeadsView';
import BrokerPoliciesTable from './components/BrokerPoliciesTable';
import BusinessUnitAdminView from './components/BusinessUnitAdminView';
import SellerView from './components/SellerView';

const BrokerDashboard: React.FC = () => {
  const { selectedBusinessUnitId: businessUnitId } = useSelectedBusinessUnit();
  const [activeTab, setActiveTab] = useState(0);

  const [searchParams, setSearchParams] = useSearchParams();
  const leadIdParam = searchParams.get('leadId');
  const initialLeadId = leadIdParam ? Number(leadIdParam) : null;

  const handleClearLeadParam = useCallback(() => {
    setSearchParams(
      (prev) => {
        prev.delete('leadId');
        return prev;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const [policiesPagination, setPoliciesPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [policiesFilter, setPoliciesFilter] = useState<GridFilterModel>({ items: [] });
  const [policiesSort, setPoliciesSort] = useState<GridSortModel>([{ field: 'createdAt', sort: 'desc' }]);

  const brokerBuParam = businessUnitId && businessUnitId > 0 ? businessUnitId : undefined;

  const {
    data: policiesData,
    isLoading: policiesLoading,
    refetch: refetchPolicies,
  } = useGetBrokerPoliciesQuery(
    {
      brokerBusinessUnitId: brokerBuParam,
      paginationModel: JSON.stringify(policiesPagination),
      filterModel: policiesFilter.items.length > 0 ? JSON.stringify(policiesFilter) : undefined,
      sortModel: JSON.stringify(policiesSort),
    },
    { skip: activeTab !== 1 }
  );

  const {
    data: dashboardData,
    isLoading: dashboardLoading,
    error: dashboardError,
  } = useGetInsuranceDashboardSummaryQuery(
    {
      ...(brokerBuParam ? { businessUnitId: brokerBuParam } : {}),
    },
    { skip: activeTab !== 0 }
  );

  const isManagerView = dashboardData?.role === 'admin' || dashboardData?.role === 'businessUnitAdmin';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 2.5, p: 2 }}>
      {/* Tab bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <SegmentedTabs tabs={['Dashboard', 'Propuestas', 'Cotizaciones']} value={activeTab} onChange={setActiveTab} />
      </Box>

      {/* Tab content with fade */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Fade in={activeTab === 2} mountOnEnter unmountOnExit timeout={300}>
          <Box sx={{ height: '100%' }}>
            <BrokerLeadsView
              brokerBusinessUnitId={brokerBuParam}
              initialLeadId={initialLeadId}
              onClearLeadParam={handleClearLeadParam}
            />
          </Box>
        </Fade>
        <Fade in={activeTab === 1} mountOnEnter unmountOnExit timeout={300}>
          <Box>
            <BrokerPoliciesTable
              rows={policiesData?.rows || []}
              rowCount={policiesData?.count || 0}
              paginationModel={policiesPagination}
              onPaginationModelChange={setPoliciesPagination}
              filterModel={policiesFilter}
              onFilterModelChange={setPoliciesFilter}
              sortModel={policiesSort}
              onSortModelChange={setPoliciesSort}
              isLoading={policiesLoading}
              onRefresh={() => {
                refetchPolicies().catch(() => {});
              }}
            />
          </Box>
        </Fade>
        <Fade in={activeTab === 0} mountOnEnter unmountOnExit timeout={300}>
          <Box sx={{ backgroundColor: SURFACE_CONTAINER_LOW, borderRadius: RADIUS_COIN, p: 3 }}>
            {dashboardLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress sx={{ color: NAVY }} />
              </Box>
            )}

            {dashboardError && <Alert severity="error">Error al cargar el dashboard</Alert>}

            {dashboardData?.role === 'seller' && dashboardData.summary && (
              <SellerView
                totalUF={dashboardData.summary.totalUF}
                policyCount={dashboardData.summary.policyCount}
                goalUF={dashboardData.goalUF}
                goalProgress={dashboardData.summary.goalProgress}
                pipeline={dashboardData.pipeline}
              />
            )}

            {isManagerView && dashboardData?.sellers && dashboardData?.totals && (
              <BusinessUnitAdminView
                sellers={dashboardData.sellers}
                totals={dashboardData.totals}
                pipeline={dashboardData.pipeline}
              />
            )}
          </Box>
        </Fade>
      </Box>
    </Box>
  );
};

export default BrokerDashboard;
